import { useEffect, useRef } from "react";
import { Renderer, Program, Mesh, Triangle } from "ogl";

const hexToRgb = (hex) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return [1, 0.5, 0.2];
    return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
};

const vertex = `#version 300 es
precision highp float;
in vec2 position;
in vec2 uv;
out vec2 vUv;
void main(){ vUv = uv; gl_Position = vec4(position,0.0,1.0); }`;

const fragment = `#version 300 es
precision highp float;
uniform vec2 iResolution;
uniform float iTime;
uniform vec3 uCustomColor;
uniform float uUseCustomColor;
uniform float uSpeed;
uniform float uDirection;
uniform float uScale;
uniform float uOpacity;
uniform vec2 uMouse;
uniform float uMouseInteractive;
uniform float uRotation; // new
out vec4 fragColor;

vec2 rotateUV(vec2 uv, vec2 center, float angle){
    float s = sin(angle);
    float c = cos(angle);
    uv -= center;
    uv = vec2(uv.x * c - uv.y * s, uv.x * s + uv.y * c);
    uv += center;
    return uv;
}

void mainImage(out vec4 o, vec2 C){
  vec2 center = iResolution.xy * 0.5;
  C = rotateUV(C, center, uRotation); // rotate coordinates
  C = (C - center) / uScale + center;
  vec2 mouseOffset = (uMouse - center) * 0.0002;
  C += mouseOffset * length(C - center) * step(0.5, uMouseInteractive);
  float i, d, z, T = iTime * uSpeed * uDirection;
  vec3 O, p, S;
  for (vec2 r = iResolution.xy, Q; ++i < 60.; O += o.w/d*o.xyz) {
    p = z*normalize(vec3(C-.5*r,r.y));
    p.z -= 4.; S = p; d = p.y-T;
    p.x += .4*(1.+p.y)*sin(d + p.x*0.1)*cos(.34*d + p.x*0.05);
    Q = p.xz *= mat2(cos(p.y+vec4(0,11,33,0)-T));
    z+= d = abs(sqrt(length(Q*Q)) - .25*(5.+S.y))/3.+8e-4;
    o = 1.+sin(S.y+p.z*.5+S.z-length(S-p)+vec4(2,1,0,8));
  }
  o.xyz = tanh(O/1e4);
}
bool finite1(float x){ return !(isnan(x) || isinf(x)); }
vec3 sanitize(vec3 c){ return vec3(finite1(c.r)?c.r:0., finite1(c.g)?c.g:0., finite1(c.b)?c.b:0.); }
void main(){
  vec4 o = vec4(0.);
  mainImage(o, gl_FragCoord.xy);
  vec3 rgb = sanitize(o.rgb);
  float intensity = (rgb.r + rgb.g + rgb.b)/3.0;
  vec3 customColor = intensity * uCustomColor;
  vec3 finalColor = mix(rgb, customColor, step(0.5, uUseCustomColor));
  float alpha = length(rgb) * uOpacity;
  fragColor = vec4(finalColor, alpha);
}`;

export default function Plasma({
                                   color = "#ffffff",
                                   speed = 1,
                                   direction = "forward",
                                   scale = 1,
                                   opacity = 1,
                                   mouseInteractive = false,
                                   rotation = 0, // new prop, in degrees
                               }) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const useCustomColor = color ? 1.0 : 0.0;
        const customColorRgb = color ? hexToRgb(color) : [1, 1, 1];
        const dirMul = direction === "reverse" ? -1.0 : 1.0;
        const rotationRad = rotation * (3.141592 / 180); // convert degrees to radians

        const renderer = new Renderer({
            webgl: 2,
            alpha: true,
            antialias: true,
            dpr: Math.min(window.devicePixelRatio || 1, 2),
        });
        const gl = renderer.gl;
        const canvas = gl.canvas;
        canvas.style.display = "block";
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        canvas.style.position = "absolute";
        canvas.style.top = "0";
        canvas.style.left = "0";
        containerRef.current.appendChild(canvas);

        const geometry = new Triangle(gl);
        const program = new Program(gl, {
            vertex,
            fragment,
            uniforms: {
                iTime: { value: 0 },
                iResolution: { value: new Float32Array([1, 1]) },
                uCustomColor: { value: new Float32Array(customColorRgb) },
                uUseCustomColor: { value: useCustomColor },
                uSpeed: { value: speed * 0.4 },
                uDirection: { value: dirMul },
                uScale: { value: scale },
                uOpacity: { value: opacity },
                uMouse: { value: new Float32Array([0, 0]) },
                uMouseInteractive: { value: mouseInteractive ? 1.0 : 0.0 },
                uRotation: { value: rotationRad }, // pass rotation
            },
        });
        const mesh = new Mesh(gl, { geometry, program });

        const setSize = () => {
            if (!containerRef.current) return;
            const { width, height } = containerRef.current.getBoundingClientRect();
            renderer.setSize(width, height);
            program.uniforms.iResolution.value[0] = width;
            program.uniforms.iResolution.value[1] = height;
        };
        setSize();
        window.addEventListener("resize", setSize);
        setTimeout(setSize, 50);

        const onMouseMove = (e) => {
            if (!mouseInteractive) return;
            const u = program.uniforms.uMouse.value;
            const rect = containerRef.current.getBoundingClientRect();
            u[0] = e.clientX - rect.left;
            u[1] = rect.height - (e.clientY - rect.top);
        };
        if (mouseInteractive) window.addEventListener("mousemove", onMouseMove);

        let raf = 0;
        const t0 = performance.now();
        const loop = (t) => {
            const timeValue = (t - t0) * 0.001;
            if (direction === "pingpong") {
                program.uniforms.uDirection.value = Math.sin(timeValue * 0.5) * dirMul;
            }
            program.uniforms.iTime.value = timeValue;
            renderer.render({ scene: mesh });
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", setSize);
            if (mouseInteractive) window.removeEventListener("mousemove", onMouseMove);
            if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
        };
    }, [color, speed, direction, scale, opacity, mouseInteractive, rotation]);

    return <div ref={containerRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}
